import Router from "vue-router";
import EmployeeList from "./components/profile/EmployeeList.vue";
import EmployerAdd from "./components/user-management/Add.vue";
import Home from "./components/landing-pages/Home.vue";
import PartnerEmployeeSearch from "./components/landing-pages/PartnerEmployeeSearch.vue";
import ManageUser from "./components/user-management/ManageUser.vue";
import * as RouteNames from "./route-names";
import PlanDetail from "./components/profile/PlanDetail.vue";
import Wizard from "./components/profile/Wizard.vue";
import GeneralInformation from "./components/profile/GeneralInfo.vue";
import Payroll from "./components/profile/Payroll.vue";
import PayrollCalendar from "./components/profile/PayrollCalendar.vue";
import PayrollReport from "./components/reports/PayrollReport.vue";
import ContributionsHistory from "./components/profile/ContributionsHistory.vue";
import EmployeeAddEdit from "./components/profile/EmployeeAddEdit.vue";
import StateList from "./components/profile/StateList.vue";
import CensusUpload from "./components/profile/CensusUpload.vue";
import CensusUploadStatus from "./components/profile/CensusUploadStatus.vue";
import EarliestInviteDate from "./components/profile/EmailInviteDate.vue";
import EmployerCensusUpload from "./components/profile/EmployerCensusUpload.vue";
import EmployeeIdUpload from "./components/profile/EmployeeIdUpload.vue";
import ManagePayroll from "./components/landing-pages/ManagePayroll.vue";
import ContributionDashboard from "./components/landing-pages/ContributionDashboard.vue";
import { Config } from "@/shared/config";
import { LoginRouteNames, LoginRoutes } from "@/shared/loginRoutes";
import NewEmployer from "./components/partner-er-management/NewEmployer.vue";
import CompanyDetails from "./components/settings/CompanyDetails.vue";
import PayrollIntegration from "./components/settings/PayrollIntegration.vue";
import PlanDetails from "./components/settings/PlanDetails.vue";
import Swinger from "./components/landing-pages/Swinger.vue";
import PartnerUsers from "./components/landing-pages/PartnerUsers.vue";
import PartnerUserAdd from "./components/landing-pages/PartnerUserAdd.vue";
import PartnerSettings from "./components/landing-pages/PartnerSettings.vue";
import AccountHolderDashboard from "./components/partner-ee-service/AccountHolderDashboard.vue";
import CashTransactions from "./components/partner-ee-service/CashTransactions.vue";
import AccountHolderExpenses from "./components/partner-ee-service/AccountHolderExpenses.vue";
import ContributionsList from "./components/partner-ee-service/ContributionsList.vue";
import InvestmentAccount from "./components/partner-ee-service/InvestmentAccount.vue";
import EmailHistory from "./components/partner-ee-service/EmailHistory.vue";
import TdaInvestmentAccountPage from "./components/partner-ee-service/TdaInvestmentAccountPage.vue";
import UmbInvestmentAccountPage from "./components/partner-ee-service/UmbInvestmentAccountPage.vue";
import InvestmentAccountPage from "./components/partner-ee-service/investment/drivewealth/InvestmentAccountPage.vue";
import DrivewealthActivity from "@/shared/components/investments/drivewealth/ActivityPage.vue";
import DrivewealthDayTrades from "./components/partner-ee-service/investment/drivewealth/DayTradePage.vue";
import DrivewealthAvailableInvestments
    from "./components/partner-ee-service/investment/drivewealth/AvailableInvestmentsPage.vue";
import DrivewealthDocuments from "./components/partner-ee-service/investment/drivewealth/DocumentsPage.vue";
import ContributionsFileUpload from "@/employer/components/landing-pages/ContributionsFileUpload.vue";
import PartnerBillingReport from "@/employer/components/reports/PartnerBillingReport.vue";
import Reports from "@/employer/components/landing-pages/Reports.vue";
import PendingContributionsReport from "@/employer/components/reports/PendingContributionsReport.vue";
import QuicksightDemoPage from "./components/landing-pages/ReportsQS.vue";
import AchContributions from "@/employer/components/profile/AchContributions.vue";
import Privacy from "@/shared/components/Privacy.vue";
import Security from "@/employer/components/settings/Security.vue";
import EmployerCommunicationsKit from "@/employer/components/resources/EmployerCommunicationsKit.vue";
import PartnerSecurity from "@/employer/components/settings/PartnerSecurity.vue";
import * as SharedRoutes from "@/shared/route-names";
import CommunicationsTab from "@/employer/components/communications/CommunicationsTab.vue";
import HsabInvestmentAccountPage from "@/employer/components/partner-ee-service/HsabInvestmentAccountPage.vue";
import LoginHistory from "@/employer/components/partner-ee-service/LoginHistory.vue";
import AccountHolderManagement from "./components/partner-ee-service/AccountHolderManagement.vue";
import ResourceForms from "./components/Forms/ResourceForms.vue";
import DrivewealthFees from "@/employee/components/investments/drivewealth/DrivewealthFees.vue";
import RequestSellPage from "@/employee/components/investments/drivewealth/RequestSellPage.vue";
import { useUserStore } from "@/shared/store/user";
import { useLoginRedirectStore } from "@/shared/store/loginRedirect";
import { useNotifierStore } from "@/shared/store/notifier";
import { CommunicationPortal, UserPermission } from "@/shared/types";
import BulkLiquidationManagement from "@/employer/components/landing-pages/BulkLiquidationManagement.vue";
import DrivewealthInvestmentAccountList
    from "@/employer/components/partner-ee-service/investment/drivewealth/DrivewealthInvestmentAccountList.vue";
import DrivewealthAccountLiquidationPage
    from "@/shared/components/investments/drivewealth/DrivewealthAccountLiquidationPage.vue";
import DrivewealthAccountSummaryView
    from "@/shared/components/investments/drivewealth/DrivewealthAccountSummaryView.vue";
import CurrentHoldings from "@/shared/components/investments/drivewealth/CurrentHoldings.vue";
import { useRolePermissionsStore } from "@/shared/store/rolePermissions";
import { useFeatureFlagStore } from "@/shared/store/featureFlag";
import EmployerAddress from "./components/settings/EmployerAddress.vue";
import MyProgramHealth from "@/employer/components/myprogram/MyProgramHealth.vue";
import MyProducts from "@/employer/components/myprogram/MyProducts.vue";
import _ from "lodash";
import MyPlans from "@/employer/components/myprogram/MyPlans.vue";
import { usePartnerSettingStore } from "@/shared/store/partnerSetting";

const routeToName = (routeName: string) => ({ name: routeName });

const router = new Router({
    routes: [
        ...LoginRoutes,
        {
            path: "/partner/:partnerId/home",
            name: RouteNames.PartnerHome,
            component: Home,
            props: route => ({
                routeToNewEmployee: () => ({
                    name: RouteNames.EmployeeInformation
                }),
                partnerId: parseInt(route.params.partnerId) || 0
            })
        },
        {
            path: "/partner/:partnerId/employee-search",
            name: RouteNames.PartnerEmployeeSearch,
            component: PartnerEmployeeSearch,
            props: route => ({
                partnerId: parseInt(route.params.partnerId) || 0,
                routeToEmployeeCustomerService: (username: string) => ({
                    name: RouteNames.PartnerAccountHolderDashboard,
                    params: {
                        partnerId: parseInt(route.params.partnerId),
                    },
                    query: {
                        username
                    }
                })
            })
        },
        {
            path: "/employer/:employerId/home",
            name: RouteNames.EmployerHome,
            component: Home,
            props: route => ({
                routeToNewEmployee: () => ({
                    name: RouteNames.EmployeeInformation
                }),
                employerId: parseInt(route.params.employerId) || 0
            })
        },
        {
            path: "/partner/:partnerId/settings",
            name: RouteNames.PartnerSettings,
            component: PartnerSettings,
            props: route => ({
                partnerId: parseInt(route.params.partnerId) || 0
            })
        },
        {
            path: "/partner/:partnerId/security",
            name: RouteNames.PartnerSecurity,
            component: PartnerSecurity,
            props: route => ({
                partnerId: parseInt(route.params.partnerId)
            })
        },
        {
            path: "/partner/:partnerId/users/:userPageNumber?",
            name: RouteNames.PartnerUsers,
            component: PartnerUsers,
            props: route => ({
                partnerId: parseInt(route.params.partnerId),
                userPageNumber: parseInt(route.params.userPageNumber) || 1
            })
        },
        {
            path: "/partner/:partnerId/addUser",
            name: RouteNames.PartnerUserAdd,
            component: PartnerUserAdd,
            props: route => ({
                routeToPartnerUserList: partnerId => ({
                    name: RouteNames.PartnerUsers,
                    params: {
                        partnerId: parseInt(route.params.partnerId),
                        userPageNumber: 1
                    }
                }),
                partnerId: parseInt(route.params.partnerId),
            })
        },
        {
            path: "/partner/:partnerId/reports/billing",
            name: RouteNames.PartnerBillingReport,
            component: PartnerBillingReport,
            props: route => ({
                partnerId: parseInt(route.params.partnerId)
            })
        },
        {
            path: "/partner/:partnerId/reports/",
            name: RouteNames.PartnerReports,
            component: Reports,
            props: route => ({
                partnerId: parseInt(route.params.partnerId)
            })
        },
        {
            path: "/partner/:partnerId/communications/accountholder",
            name: RouteNames.PartnerCommunicationsAccountHolder,
            component: CommunicationsTab,
            props: route => ({
                partnerId: parseInt(route.params.partnerId),
                accountHolderRoute: RouteNames.PartnerCommunicationsAccountHolder,
                employerRoute: RouteNames.PartnerCommunicationsEmployer,
                portal: CommunicationPortal.Employee
            })
        },
        {
            path: "/partner/:partnerId/communications/employer",
            name: RouteNames.PartnerCommunicationsEmployer,
            component: CommunicationsTab,
            props: route => ({
                partnerId: parseInt(route.params.partnerId),
                accountHolderRoute: RouteNames.PartnerCommunicationsAccountHolder,
                employerRoute: RouteNames.PartnerCommunicationsEmployer,
                portal: CommunicationPortal.Employer
            })
        },
        {
            path: "/employer/:employerId/reports/",
            name: RouteNames.EmployerReports,
            component: Reports,
            props: route => ({
                employerId: parseInt(route.params.employerId)
            })
        },
        {
      path: "/partner/:partnerId/ResourceForms/",
      name: RouteNames.PartnerResourceForms,
      component: ResourceForms,
      props: route => ({
        partnerId: parseInt(route.params.partnerId)
      })
    },
    {
      path: "/employer/:employerId/ResourceForms/",
      name: RouteNames.EmployerResourceForms,
      component: ResourceForms,
      props: route => ({
        employerId: parseInt(route.params.employerId)
      })
    },
    {
            path: "/",
            name: RouteNames.Home,
            component: Swinger,
            props: route => ({
                routeToLogin: () => ({
                    name: LoginRouteNames.Login
                }),
                routeToEmployerHome: employerId => ({
                    name: RouteNames.EmployerHome,
                    params: {
                        employerId,
                        routeToNewEmployee: () => ({
                            name: RouteNames.EmployeeInformation
                        })
                    }
                }),
                routeToPartnerHome: partnerId => ({
                    name: RouteNames.PartnerHome,
                    params: {
                        partnerId,
                        routeToNewEmployee: () => ({
                            name: RouteNames.EmployeeInformation
                        })
                    }
                }),
                routeToPartnerEmployeeSearch: partnerId => ({
                    name: RouteNames.PartnerEmployeeSearch,
                    params: {
                        partnerId
                    }
                }),
                routeToPartnerUsers: partnerId => ({
                    name: RouteNames.PartnerUsers,
                    params: {
                        partnerId
                    }
                }),
                routeToBulkLiquidation: partnerId => ({
                    name: RouteNames.BulkLiquidationManagement,
                    params: {
                        partnerId,
                        page: 1
                    }
                }),
            })
        },
        {
            path: "/settings/add-employer-user",
            name: RouteNames.SettingsAddEmployerUser,
            component: EmployerAdd
        },
        {
            path: "/manage-funding-file/:page?",
            name: RouteNames.ManagePayrollFundingFile,
            component: ContributionsFileUpload,
            props: route => ({
                page: parseInt(route.params.page) || 1,
                routeToManagePayrollFundingFile: page => ({
                    name: RouteNames.ManagePayrollFundingFile,
                    params: {
                        page
                    }
                }),
            })
        },
        {
            path: "/contributions/pending-contributions/:page?",
            name: RouteNames.PendingContributions,
            component: PendingContributionsReport,
            props: route => ({
                routeToPendingContributionsList: page => ({
                    name: RouteNames.PendingContributions,
                    params: {
                        page
                    }
                }),
                page: parseInt(route.params.page) || 1
            })
        },
        {
            path: "/myprogram/program-health",
            name: RouteNames.ProgramHealth,
            component: MyProgramHealth,
        },
        {
            path: "/myprogram/my-plans",
            name: RouteNames.MyPlans,
            component: MyPlans,
        },
        {
            path: "/myprogram/program-products",
            name: RouteNames.ProgramProducts,
            component: MyProducts,
            beforeEnter: (to, from, next) => {
                const userStore = useUserStore();
                const partnerSettingStore = usePartnerSettingStore();

                if (!partnerSettingStore.isLoaded) {
                    partnerSettingStore.loadEmployerPartnerSettings({
                        partnerId: userStore.currentEmployer.partnerId,
                        employerId: userStore.currentEmployer.employerId
                    }).then(() => {
                        if (partnerSettingStore.settings.resourceVisibilityEnabled) {
                            return next();
                        }
                    });
                } else {
                    if (partnerSettingStore.settings.resourceVisibilityEnabled) {
                        return next();
                    }
                }
            },
        },
        {
            path: "/employees/invite",
            name: RouteNames.EmployeesInvite,
            component: EarliestInviteDate,
        },
        {
            path: "/employees/census-upload/:page?",
            name: RouteNames.EmployerCensusUpload,
            component: EmployerCensusUpload,
            props: route => ({
                page: parseInt(route.params.page) || 1,
                routeToCensusFileUploadView: page => ({
                    name: RouteNames.EmployerCensusUpload,
                    params: {
                        page
                    }
                }),
            })
        },
        {
            path: "/employees/employeeid-upload/:page?",
            name: RouteNames.EmployeeIdUpload,
            component: EmployeeIdUpload,
            props: route => ({
                page: parseInt(route.params.page) || 1,
                routeToEmployeeIdFileUploadView: page => ({
                    name: RouteNames.EmployeeIdUpload,
                    params: {
                        page
                    }
                }),
            })
        },
        {
            path: "/employees/:page?",
            name: RouteNames.Employees,
            component: EmployeeList,
            props: route => ({
                routeToEmployeeEdit: employeeId => ({
                    name: RouteNames.EmployeeInformation,
                    params: {
                        employeeId
                    }
                }),
                routeToEmployeeList: page => ({
                    name: RouteNames.Employees,
                    params: {
                        page
                    }
                }),
                routeToCensusUpload: routeToName(RouteNames.CensusUpload),
                routeToManagePayroll: routeToName(RouteNames.ContributionsNextPayroll),
                page: parseInt(route.params.page) || 1
            })
        },
        {
            path: "/settings/manage-employer-user",
            name: RouteNames.SettingsManageEmployerUser,
            component: ManageUser,
            beforeEnter: (to, from, next) => {
                const featureFlagStore = useFeatureFlagStore();
                const userStore = useUserStore();
                if (!featureFlagStore.flags.hsa9516ManageSystemUsersEnabled  && userStore.isApexUser()) {
                    return next({
                        name: RouteNames.SettingsCompanyDetails,
                        hash: to.hash,
                        replace: true,
                    });
                }
                return next();
            },
        },
        {
            path: "/settings/security",
            name: SharedRoutes.Security,
            component: Security
        },
        {
            path: "/contributions",
            name: RouteNames.Contributions,
            component: ContributionDashboard
        },
        {
            path: "/resources/communications-kit",
            name: RouteNames.EmployerCommunicationsKit,
            component: EmployerCommunicationsKit
        },
        {
            path: "/contributions/ach/:page?",
            name: RouteNames.AchContributions,
            component: AchContributions,
            props: route => ({
                routeToAchContributions: page => ({
                    name: RouteNames.AchContributions,
                    params: {
                        page
                    }
                }),
                page: parseInt(route.params.page) || 1
            })
        },
        {
            path: "/contributions/next-payroll/:page?",
            name: RouteNames.ContributionsNextPayroll,
            component: ManagePayroll,
            props: route => ({
                routeToManagePayroll: page => ({
                    name: RouteNames.ContributionsNextPayroll,
                    params: {
                        page
                    },
                    routeToEmployeeEdit: employeeId => ({
                        name: RouteNames.EmployeeInformation,
                        params: {
                            employeeId
                        }
                    }),
                }),
                page: parseInt(route.params.page) || 1
            })
        },
        {
            path: "/onboarding/:step?",
            name: RouteNames.Onboarding,
            component: Wizard,
            props: route => {
                return {
                    goToStep: step => ({
                        name: RouteNames.Onboarding,
                        params: {
                            step
                        }
                    }),
                    routeToFinish: () => ({
                        name: RouteNames.Home
                    }),
                    routeToWizardComplete: () => ({
                        name: RouteNames.Home,
                    }),
                    step: parseInt(route.params.step)
                };
            }
        },
        {
            path: "/profile/general-info",
            name: RouteNames.ProfileInformation,
            component: GeneralInformation,
        },
        {
            path: "/profile/payroll",
            name: RouteNames.PayrollInformation,
            component: Payroll,
        },
        {
            path: "/profile/plandetail",
            name: RouteNames.PlanDetailInformationRouteName,
            component: PlanDetail,
        },
        {
            path: "/employees/employee/:employeeId?/:tab?",
            name: RouteNames.EmployeeInformation,
            component: EmployeeAddEdit,
            props: route => ({
                routeToEmployeeList: routeToName(RouteNames.Employees),
                employeeId: parseInt(route.params.employeeId) || 0
            })
        },
        {
            path: "/profile/census-upload",
            name: RouteNames.CensusUpload,
            component: CensusUpload,
            props: {
                afterFileUploaded: watchedFile => router.push({
                    name: RouteNames.CensusStatus,
                    params: {
                        watchedFile
                    }
                }),
                bucketName: Config.Census.BucketName,
                routeToStateList: routeToName(RouteNames.StateListRouteName)
            }
        },
        {
            path: "/profile/census-upload-states",
            name: RouteNames.StateListRouteName,
            component: StateList
        },
        {
            path: "/contributions/payroll-calendar",
            name: RouteNames.ContributionsCalendar,
            component: PayrollCalendar,
            props: {
                saveCaption: "Save",
                fetchEnrollmentData: true
            }
        },
        {
            path: "/profile/census-upload/:watchedFile",
            name: RouteNames.CensusStatus,
            component: CensusUploadStatus,
            props: route => {
                return {
                    watchedFile: route.params.watchedFile,
                    bucketName: Config.Census.BucketName
                };
            }
        },
        {
            path: "/partner/:partnerId/new-employer/:employerId/:step?",
            name: RouteNames.PartnerNewEmployer,
            component: NewEmployer,
            props: route => {
                const partnerId = parseInt(route.params.partnerId) || 0;
                return {
                    goToStep: (employerId, step, edit) => ({
                        name: RouteNames.PartnerNewEmployer,
                        params: {
                            partnerId,
                            step,
                            employerId
                        },
                        query: {
                            edit
                        }
                    }),
                    routeToFinish: () => ({
                        name: RouteNames.PartnerHome,
                        params: {
                            partnerId
                        }
                    }),
                    routeToWizardComplete: () => ({
                        name: RouteNames.PartnerHome,
                        params: {
                            partnerId
                        }
                    }),
                    edit: route.query.edit,
                    employerName: route.params.employerName,
                    step: parseInt(route.params.step),
                    partnerId,
                    employerId: parseInt(route.params.employerId) || 0
                };
            }
        },
        {
            path: "/partner/:partnerId/account-holder/dashboard",
            name: RouteNames.PartnerAccountHolderDashboard,
            component: AccountHolderDashboard,
            props: route => ({
                username: route.query.username,
                partnerId: parseInt(route.params.partnerId) || 0,
                routeToContributions: { name: RouteNames.PartnerAccountHolderContribution, query: { username: route.query.username, tab: route.query.tab }}
            })
        },
        {
            path: "/partner/:partnerId/account-holder/management",
            name: RouteNames.PartnerAccountHolderManagement,
            component: AccountHolderManagement,
            props: route => ({
                username: route.query.username,
                partnerId: parseInt(route.params.partnerId) || 0
            })
        },
        {
            path: "/partner/:partnerId/account-holder/cash-transactions",
            name: RouteNames.PartnerAccountHolderCashTrx,
            component: CashTransactions,
            props: route => {
                const pageNumber = route.query && route.query.page ? parseInt(<string>route.query.page, 10) : 1;
                const filter = parseInt(<string>route.query.transactionFilter) || 0;

                return {
                    username: route.query.username,
                    partnerId: parseInt(route.params.partnerId) || 0,
                    page: pageNumber,
                    transactionFilter: filter,
                    routeToPages: (page, filter) => ({
                        name: RouteNames.PartnerAccountHolderCashTrx,
                        params: {
                            partnerId: parseInt(route.params.partnerId),
                        },
                        query: {
                            username: route.query.username,
                            page,
                            transactionFilter: filter
                        }
                    })
                };
            }
        },
        {
            path: "/partner/:partnerId/account-holder/expenses",
            name: RouteNames.PartnerAccountHolderExpenses,
            component: AccountHolderExpenses,
            props: route => {
                return {
                    username: route.query.username,
                    partnerId: parseInt(route.params.partnerId) || 0,
                    routeToPages: () => ({
                        name: RouteNames.PartnerAccountHolderExpenses,
                        params: {
                            partnerId: parseInt(route.params.partnerId),
                        },
                        query: {
                            username: route.query.username
                        }
                    })
                };
            }
        },
        {
            path: "/partner/:partnerId/account-holder/contributions",
            name: RouteNames.PartnerAccountHolderContribution,
            component: ContributionsList,
            props: route => {
                const pageNumber = route.query && route.query.page ? parseInt(<string>route.query.page, 10) : 1;
                const schedulesPage = route.query && route.query.schedulesPage
                ? parseInt(<string>route.query.schedulesPage, 10)
                : 1;
                const tab = (route.query && route.query.tab) || "activity";
                const activityPage = route.query && route.query.activityPage
                ? parseInt(<string>route.query.activityPage, 10)
                : 1;
            return {
              username: route.query.username,
              partnerId: parseInt(route.params.partnerId) || 0,
              page: pageNumber,
              routeToPages: page => ({
                name: RouteNames.PartnerAccountHolderContribution,
                params: {
                  partnerId: parseInt(route.params.partnerId),
                },
                query: {
                  username: route.query.username,
                  page
                }
              }),
              schedulesPage: isNaN(schedulesPage) ? 1 : schedulesPage,
              tab,
              activityPage: isNaN(activityPage) ? 1 : activityPage
            };
      }
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account",
      name: RouteNames.PartnerAccountHolderInvestment,
      component: InvestmentAccount,
      beforeEnter: (to, from, next) => {
          if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
              // do not navigate
          } else {
              next();
          }
      },
      props: route => {
        const pageNumber = route.query && route.query.page ? parseInt(<string>route.query.page, 10) : 1;
        return {
          username: route.query.username,
          partnerId: parseInt(route.params.partnerId) || 0,
          page: pageNumber,
          routeToPages: page => ({
            name: RouteNames.PartnerAccountHolderInvestment,
            params: {
              partnerId: parseInt(route.params.partnerId),
            },
            query: {
              username: route.query.username,
              page
            }
          })
        };
      }
    },
    {
      path: "/partner/:partnerId/email-history",
      name: RouteNames.EmailHistory,
      component: EmailHistory,
      props: route => {
        return {
          partnerId: parseInt(route.params.partnerId) || 0,
          email: route.query.email,
        };
      }
    },
    {
      path: "/partner/:partnerId/login-history",
      name: RouteNames.LoginHistory,
      component: LoginHistory,
      props: route => {
        return {
          partnerId: parseInt(route.params.partnerId) || 0,
          username: route.query.username
        };
      }
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth",
      name: SharedRoutes.DrivewealthInvestmentAccount,
      component: InvestmentAccountPage,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => {
        const pageNumber = route.query && route.query.page ? parseInt(<string>route.query.page, 10) : 1;
        return {
          username: route.query.username,
          partnerId: parseInt(route.params.partnerId) || 0,
          page: pageNumber,
          routeToPages: page => ({
            name: SharedRoutes.DrivewealthInvestmentAccount,
            params: {
              partnerId: parseInt(route.params.partnerId),
            },
            query: {
              username: route.query.username,
              page
            }
          })
        };
      },
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth/v2/:accountId",
      name: SharedRoutes.DrivewealthInvestmentAccountV2,
      component: InvestmentAccountPage,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => {
        const pageNumber = route.query && route.query.page ? parseInt(<string>route.query.page, 10) : 1;
        return {
          username: route.query.username,
          partnerId: parseInt(route.params.partnerId) || 0,
          accountId: parseInt(route.params.accountId) || 0,
          page: pageNumber,
          routeToMultiAccountInvestmentView: page => ({
            name: SharedRoutes.DrivewealthInvestmentAccountV2,
            params: {
              partnerId: parseInt(route.params.partnerId),
              accountId: parseInt(route.params.accountId)
            },
            query: {
              username: route.query.username,
              page
            }
          })
        };
      }
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth/v2/:accountId/liquidate",
      name: SharedRoutes.DrivewealthAccountLiquidation,
      component: DrivewealthAccountLiquidationPage,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => {
        return {
          username: route.query.username,
          partnerId: parseInt(route.params.partnerId) || 0,
          accountId: parseInt(route.params.accountId) || 0
        };
      }
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/umb",
      name: RouteNames.UmbInvestmentAccount,
      component: UmbInvestmentAccountPage,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => {
        const pageNumber = route.query && route.query.page ? parseInt(<string>route.query.page, 10) : 1;
        return {
          username: route.query.username,
          partnerId: parseInt(route.params.partnerId) || 0,
          page: pageNumber,
          routeToPages: page => ({
            name: RouteNames.UmbInvestmentAccount,
            params: {
              partnerId: parseInt(route.params.partnerId),
            },
            query: {
              username: route.query.username,
              page
            }
          })
        };
      }
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/tda",
      name: RouteNames.TdaInvestmentAccount,
      component: TdaInvestmentAccountPage,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => {
        const pageNumber = route.query && route.query.page ? parseInt(<string>route.query.page, 10) : 1;
        return {
          username: route.query.username,
          partnerId: parseInt(route.params.partnerId) || 0,
          page: pageNumber,
          routeToPages: page => ({
            name: RouteNames.TdaInvestmentAccount,
            params: {
              partnerId: parseInt(route.params.partnerId),
            },
            query: {
              username: route.query.username,
              page
            }
          })
        };
      }
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth/investment-activity",
      name: SharedRoutes.DrivewealthInvestmentActivity,
      component: DrivewealthActivity,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => ({
        username: route.query.username,
        partnerId:  parseInt(route.params.partnerId) || 0
      })
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth/investment-activity/v2/:accountId",
      name: SharedRoutes.DrivewealthInvestmentActivityV2,
      component: DrivewealthActivity,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => ({
        username: route.query.username,
        partnerId:  parseInt(route.params.partnerId) || 0,
        accountId:  parseInt(route.params.accountId) || 0
      })
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth/activity/transfers",
      name: SharedRoutes.DrivewealthInvestmentActivityTransfers,
      component: DrivewealthActivity,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => ({
        isTransfers: true,
        username: route.query.username,
        partnerId:  parseInt(route.params.partnerId) || 0
      })
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth/activity/history",
      name: SharedRoutes.DrivewealthInvestmentActivityHistory,
      component: DrivewealthActivity,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => ({
        isHistory: true,
        username: route.query.username,
        partnerId:  parseInt(route.params.partnerId) || 0
      })
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth/activity/transfers/v2/:accountId",
      name: SharedRoutes.DrivewealthInvestmentActivityTransfersV2,
      component: DrivewealthActivity,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => ({
        isTransfers: true,
        username: route.query.username,
        partnerId:  parseInt(route.params.partnerId) || 0,
        accountId:  parseInt(route.params.accountId) || 0
      })
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth/activity/history/v2/:accountId",
      name: SharedRoutes.DrivewealthInvestmentActivityHistoryV2,
      component: DrivewealthActivity,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => ({
        isHistory: true,
        username: route.query.username,
        partnerId:  parseInt(route.params.partnerId) || 0,
        accountId:  parseInt(route.params.accountId) || 0
      })
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth/investment-documents",
      name: SharedRoutes.DrivewealthInvestmentDocuments,
      component: DrivewealthDocuments,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => {
        const pageNumber = route.query && route.query.page ? parseInt(<string>route.query.page, 10) : 1;
        return {
          username: route.query.username,
          partnerId: parseInt(route.params.partnerId) || 0,
          pageNumber,
          routeToPages: page => ({
            name: SharedRoutes.DrivewealthInvestmentDocuments,
            params: {
              partnerId: parseInt(route.params.partnerId),
            },
            query: {
              username: route.query.username,
              pageNumber
            }
          })
        };
      }
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth/investment-documents/v2/:accountId/:pageNumber",
      name: SharedRoutes.DrivewealthInvestmentDocumentsV2,
      component: DrivewealthDocuments,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => ({
        routeToDrivewealthDocumentsV2: (accountId, pageNumber, partnerId, username) => ({
          name: SharedRoutes.DrivewealthInvestmentDocumentsV2,
          params: {
            accountId,
            partnerId,
            pageNumber
          },
          query: {
            username,
          }
        }),
        accountId: parseInt(route.params.accountId),
        partnerId: parseInt(route.params.partnerId),
        pageNumber: parseInt(route.params.pageNumber) || 1,
        username: route.query.username
      })
    },
    {
        path: "/partner/:partnerId/account-holder/investment-account/drivewealth/fees/v2/:accountId",
        name: SharedRoutes.DrivewealthInvestmentFeesV2,
        component: DrivewealthFees,
        beforeEnter: (to, from, next) => {
          if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
              // do not navigate
          } else {
              next();
          }
      },
        props: route => ({
            routeToDrivewealthFeesV2: (accountId, partnerId, username) => ({
                name: SharedRoutes.DrivewealthInvestmentFeesV2,
                params: {
                    accountId,
                    partnerId,
                },
                query: {
                    username,
                }
            }),
            accountId: parseInt(route.params.accountId),
            partnerId: parseInt(route.params.partnerId),
            username: route.query.username
        })
    },
    {
        path: "/partner/:partnerId/account-holder/investment-account/drivewealth/v2/sell-instrument/:accountId/:symbol?",
        name: RouteNames.DrivewealthSellInstrumentV2,
        component: RequestSellPage,
        beforeEnter: (to, from, next) => {
          if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
              // do not navigate
          } else {
              next();
          }
      },
        meta: {
            notifies_forward: true,
        },
        props: route => ({
            accountId: parseInt(route.params.accountId),
            partnerId: parseInt(route.params.partnerId),
            symbol: route.params.symbol.toUpperCase(),
            doubleConfirm: false,
            username: route.query.username
        })
    },
    {
        path: "/partner/:partnerId/account-holder/investment-account/drivewealth/v2/sell-instrument-confirm/:accountId/:symbol?",
        name: RouteNames.DrivewealthSellInstrumentConfirmV2,
        component: RequestSellPage,
        beforeEnter: (to, from, next) => {
          if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
              // do not navigate
          } else {
              next();
          }
      },
        meta: {
          notifies_forward: true
        },
        props: route => ({
            accountId: parseInt(route.params.accountId),
            partnerId: parseInt(route.params.partnerId),
            symbol: route.params.symbol.toUpperCase(),
            doubleConfirm: true,
            username: route.query.username
        })
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth/investment-daytrades",
      name: SharedRoutes.DrivewealthInvestmentDayTrades,
      component: DrivewealthDayTrades,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => {
        const pageNumber = route.query && route.query.page ? parseInt(<string>route.query.page, 10) : 1;
        return {
          username: route.query.username,
          partnerId: parseInt(route.params.partnerId) || 0,
          page: pageNumber,
          routeToPages: page => ({
            name: SharedRoutes.DrivewealthInvestmentDayTrades,
            params: {
              partnerId: parseInt(route.params.partnerId),
            },
            query: {
              username: route.query.username,
              page
            }
          })
        };
      }
    },
        {
            path: "/partner/:partnerId/account-holder/investment-account/drivewealth/v2/account-summary/:accountId",
            name: SharedRoutes.DrivewealthAccountSummary,
            component: DrivewealthAccountSummaryView,
            beforeEnter: (to, from, next) => {
                if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
                    // do not navigate
                } else {
                    next();
                }
            },
            meta: {
                notifies_forward: true,
            },
            props: route => ({
                accountId: parseInt(route.params.accountId),
                partnerId: parseInt(route.params.partnerId),
                username: route.query.username
            })
        },
        {
            path: "/partner/:partnerId/account-holder/investment-account/drivewealth/v2/current-holdings/:accountId",
            name: SharedRoutes.DrivewealthCurrentHoldings,
            component: CurrentHoldings,
            beforeEnter: (to, from, next) => {
                if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
                    // do not navigate
                } else {
                    next();
                }
            },
            meta: {
                notifies_forward: true,
            },
            props: route => ({
                accountId: parseInt(route.params.accountId),
                partnerId: parseInt(route.params.partnerId),
                username: route.query.username
            })
        },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth/investment-daytrades/v2/:accountId",
      name: SharedRoutes.DrivewealthInvestmentDayTradesV2,
      component: DrivewealthDayTrades,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => {
        const pageNumber = route.query && route.query.page ? parseInt(<string>route.query.page, 10) : 1;
        return {
          username: route.query.username,
          partnerId: parseInt(route.params.partnerId) || 0,
          accountId: parseInt(route.params.accountId) || 0,
          page: pageNumber,
          routeToPages: page => ({
            name: SharedRoutes.DrivewealthInvestmentDayTrades,
            params: {
              partnerId: parseInt(route.params.partnerId),
              accountId: parseInt(route.params.accountId)
            },
            query: {
              username: route.query.username,
              page
            }
          })
        };
      }
    },
    {
        path: "/partner/:partnerId/account-holder/investment-account/drivewealth/investment-account-selection",
        name: SharedRoutes.DrivewealthInvestmentAccountList,
        component: DrivewealthInvestmentAccountList,
        beforeEnter: (to, from, next) => {
          if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
              // do not navigate
          } else {
              next();
          }
      },
        props: route => {
            return {
                username: route.query.username,
                partnerId: parseInt(route.params.partnerId) || 0,
            };
        }
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth/available-investments",
      name: SharedRoutes.DrivewealthAvailableInvestments,
      component: DrivewealthAvailableInvestments,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => {
        const pageNumber = route.query && route.query.page ? parseInt(<string>route.query.page, 10) : 1;
        return {
          username: route.query.username,
          partnerId: parseInt(route.params.partnerId) || 0,
          page: pageNumber,
          routeToPages: page => ({
            name: SharedRoutes.DrivewealthAvailableInvestments,
            params: {
              partnerId: parseInt(route.params.partnerId),
            },
            query: {
              username: route.query.username,
              page
            }
          })
        };
      }
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/drivewealth/available-investments/v2/:accountId",
      name: SharedRoutes.DrivewealthAvailableInvestmentsV2,
      component: DrivewealthAvailableInvestments,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => {
        const pageNumber = route.query && route.query.page ? parseInt(<string>route.query.page, 10) : 1;
        return {
          username: route.query.username,
          partnerId: parseInt(route.params.partnerId) || 0,
          accountId: parseInt(route.params.accountId) || 0,
          page: pageNumber,
          routeToPages: page => ({
            name: SharedRoutes.DrivewealthAvailableInvestments,
            params: {
              partnerId: parseInt(route.params.partnerId),
              accountId: parseInt(route.params.accountId)
            },
            query: {
              username: route.query.username,
              page
            }
          })
        };
      }
    },
    {
      path: "/partner/:partnerId/account-holder/investment-account/hsab",
      name: RouteNames.HsabInvestmentAccount,
      component: HsabInvestmentAccountPage,
      beforeEnter: (to, from, next) => {
        if (!hasAccess([ UserPermission.PartnerPortalInvestmentView])) {
            // do not navigate
        } else {
            next();
        }
    },
      props: route => ({
        username: route.query.username,
        partnerId: parseInt(route.params.partnerId) || 0
      })
    },
    {
      path: "/contributions/payroll-history",
      name: RouteNames.PayrollReport,
      component: PayrollReport
    },
    {
      path: "/contributions/contributions-history",
      name: RouteNames.ContributionsHistory,
      component: ContributionsHistory
    },
    {
      path: "/settings/company-details",
      name: RouteNames.SettingsCompanyDetails,
      component: CompanyDetails,
      beforeEnter: (to, from, next) => {
            const userStore = useUserStore();
            if (userStore.isApexUser()) {
                return next({
                    name: RouteNames.SettingsEmployerAddress,
                    hash: to.hash,
                    replace: true,
                });
            }
            return next();
        },
    },
    {
      path: "/settings/employer-address",
      name: RouteNames.SettingsEmployerAddress,
      component: EmployerAddress,
      beforeEnter: (to, from, next) => {
            const userStore = useUserStore();
            if (!userStore.isApexUser()) {
                return next({
                    name: RouteNames.SettingsCompanyDetails,
                    hash: to.hash,
                    replace: true,
                });
            }
            return next();
        },
    },
    {
      path: "/settings/hsa-plan-details",
      name: RouteNames.SettingsHsaPlanDetails,
      component: PlanDetails
    },
    {
      path: "/settings/payroll-integration",
      name: RouteNames.SettingsPayrollIntegration,
      component: PayrollIntegration
    },
    {
      path: "/privacy",
      name: RouteNames.Privacy,
      component: Privacy
    },
    {
      path: "/contributions-file-upload",
      name: RouteNames.ContributionsFileUpload,
      component: ContributionsFileUpload
    },
    {
      path: "/partner/:partnerId/quicksight-list",
      component: QuicksightDemoPage,
      name: RouteNames.PartnerReportsListQs,
      props: route => ({
        partnerId: parseInt(route.params.partnerId) || 0,
        routeToRenderReport: (dashboardId: string) => ({
          name: RouteNames.PartnerReportRenderQs,
          params: {
            partnerId: parseInt(route.params.partnerId),
            dashboardId
          }
        })
      })
    },
    {
        path: `/partner/:partnerId/bulk-liquidation/:page?`,
        component: BulkLiquidationManagement,
        name: RouteNames.BulkLiquidationManagement,
        props: route => ({
            routeToBulkLiquidationManagement: (partnerId, page) => ({
                name: RouteNames.BulkLiquidationManagement,
                params: {
                    partnerId,
                    page
                }
            }),
            partnerId: parseInt(route.params.partnerId) || 0,
            page: parseInt(route.params.page) || 1
        })
    },
    {
      path: "/partner/:partnerId/quicksight/:dashboardId",
      component: QuicksightDemoPage,
      name: RouteNames.PartnerReportRenderQs,
      meta: {
        "full-render-main-component": 1
      },
      props: route => ({
        dashboardId: route.params.dashboardId,
        partnerId: route.params.partnerId
      })
    },
    {
      path: "/employer/:employerId/quicksight-list",
      component: QuicksightDemoPage,
      name: RouteNames.EmployerReportsListQs,
      props: route => ({
        employerId: parseInt(route.params.employerId) || 0,
        routeToRenderReport: (dashboardId: string) => ({
          name: RouteNames.EmployerReportRenderQs,
          params: {
            employerId: parseInt(route.params.employerId),
            dashboardId
          }
        })
      })
    },
    {
      path: "/employer/:employerId/quicksight/:dashboardId",
      component: QuicksightDemoPage,
      name: RouteNames.EmployerReportRenderQs,
      meta: {
        "full-render-main-component": 1
      },
      props: route => ({
        dashboardId: route.params.dashboardId,
        employerId: route.params.employerId
      })
    },
    {
      path: "*",
      redirect: "/"
    }
  ],
  scrollBehavior (from, to) {
      if (_.get(to, "name") !== _.get(from, "name")) {
          return { x: 0, y: 0 }; // scroll to top when the route name changes
      }
      // void return value will be falsey, and that will cause the scroll pos to be retained
  }
});

router.beforeEach((to, from, next) => {
    const userStore = useUserStore();
    const loginRedirectStore = useLoginRedirectStore();

    const notifierStore = useNotifierStore();

    if (!from.matched.some(r => r.meta.notifies_forward)) {
        notifierStore.clearAlerts();
    }

    const userLoggedIn = userStore.loggedIn;
    if (!to.matched.some(r => r.meta.allowAnonymous) && !userLoggedIn) {
        if (to.name !== LoginRouteNames.Login && !!to.name
            && (to.name !== RouteNames.EmployerHome && to.name !== RouteNames.PartnerHome)) {
            loginRedirectStore.setRedirectRouteObject({ ...to });
        }
        next({
            name: LoginRouteNames.Login,
            query: to.query.returnUrl ? to.query : {
                ...to.query,
                returnUrl: to.path
            }
        });
    }

    // prevent employer users from landing on the wrong login URL
    if (!userLoggedIn && (to.name === LoginRouteNames.AdminOktaLogin || to.name === LoginRouteNames.OktaLogin)) {
        next({
            name: LoginRouteNames.Login,
            query: to.query.returnUrl ? to.query : {
                ...to.query,
                returnUrl: to.path
            }
        });
    }

    const employerOnboarded = userStore.employerOnboarded;
    const employerOnboardingProgress = userStore.employerOnboardingProgress;
    const redirectRouteObject = loginRedirectStore.routeObject;

    if (userLoggedIn &&
        to.name !== RouteNames.PartnerHome &&
        to.name !== RouteNames.EmployerHome &&
        redirectRouteObject) {
        loginRedirectStore.setRedirectRouteObject(null);
        next({ ...redirectRouteObject });
        return;
    }

    // redirect to home view if the employer is logged in
    // prevent them from being able to see login/create-account when already logged in

    if (userLoggedIn && to.name === LoginRouteNames.Login) {
        next({ name: RouteNames.Home });
        return;
    }

    if (userLoggedIn && to.name === RouteNames.Signup) {
        next({ name: RouteNames.Home });
        return;
    }

    // redirect to wizard if the employer has not been onboarded

    if (employerOnboarded === true && to.name === RouteNames.Onboarding) {
        next({ name: RouteNames.Home });
        return;
    }

    if (employerOnboarded === null || employerOnboarded === true) {
        next();
    } else {
        if (to.name !== RouteNames.Onboarding) {
            next({ name: RouteNames.Onboarding, params: { step: employerOnboardingProgress.currentStep || 1 } });
        } else {
            next();
        }
    }
});

function hasAccess (permissionsToCheck: UserPermission[]): boolean {
    const hasAccess = permissionsToCheck.filter(
            perm => useRolePermissionsStore().userPermissions.some(
                userPerm => perm === userPerm)).length > 0;
    return hasAccess;
}

export default router;
